import CircleStyle from "ol/style/Circle";
import {Fill, Stroke, Style, Text} from "ol/style";
import {MINT, NEUTRAL, PRIMARY} from "./consts.common";
import Feature, {FeatureLike} from "ol/Feature";
import {TPlanMarkerProperty} from "../components/organisms/plan/PlanMap";

export const isBrowser = () => typeof window !== "undefined";

export function objectToStringArray(o: any, arraySupported?: boolean): string[] {
  const keyValuePairs: string[] = [];
  Object.keys(o)
        .forEach((key) => {
          if (Array.isArray(o[key])) {
            if (arraySupported) {
              keyValuePairs.push(`${encodeURIComponent(key)}=${encodeURIComponent(o[key].join(","))}`);
            }
            else {
              o[key].forEach((str: any) => {
                keyValuePairs.push(`${encodeURIComponent(key)}=${encodeURIComponent(str)}`);
              });
            }
          }
          else {
            keyValuePairs.push(`${encodeURIComponent(key)}=${encodeURIComponent(o[key])}`);
          }
        });
  return keyValuePairs;
}

export function objToQueryString(obj?: any, arraySupported?: boolean) {
  if (!obj) {
    return "";
  }
  let keyValuePairs: string[] = [];
  if (Array.isArray(obj)) {
    obj.forEach((o) => {
      keyValuePairs = [
        ...keyValuePairs, ...objectToStringArray(o, arraySupported),
      ];
    });
  }
  else {
    keyValuePairs = objectToStringArray(obj, arraySupported);
  }
  return keyValuePairs.join("&");
}

export function hexColorDelta(hex1: string, hex2: string) {
  // get red/green/blue int values of hex1
  var r1 = parseInt(hex1.substring(0, 2), 16);
  var g1 = parseInt(hex1.substring(2, 4), 16);
  var b1 = parseInt(hex1.substring(4, 6), 16);
  // get red/green/blue int values of hex2
  var r2 = parseInt(hex2.substring(0, 2), 16);
  var g2 = parseInt(hex2.substring(2, 4), 16);
  var b2 = parseInt(hex2.substring(4, 6), 16);
  // calculate differences between reds, greens and blues
  var r = 255 - Math.abs(r1 - r2);
  var g = 255 - Math.abs(g1 - g2);
  var b = 255 - Math.abs(b1 - b2);
  // limit differences between 0 and 1
  r /= 255;
  g /= 255;
  b /= 255;
  // 0 means opposite colors, 1 means same colors
  return (r + g + b) / 3;
}

const styleCache: any = {};

function isHexColor(color: string) {
  return /^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{6})$/.test(color);
}

export function createCluster(resolution: number, size: number, radius: number, fillColor: string, strokeColor: string, alpha: number) {
  const key = resolution + fillColor + size;
  if (styleCache[key]) return styleCache[key];
  if (isHexColor(fillColor)) fillColor = `${fillColor}${alpha}`;
  const style = [
    new Style({
      image: new CircleStyle({
        radius: radius, stroke: new Stroke({
          width: 2, color: strokeColor ? strokeColor : '#fff',
        }), fill: new Fill({
          color: fillColor ? fillColor : "#3399CC",
        }),
      }), text: new Text({
        font: "bold 13px Spoqa Han Sans Neo", text: size.toString(), fill: new Fill({
          color: "#fff",
        }),
      }),
    }),
  ];
  styleCache[key] = style;
  return styleCache[key];
}

export function styleShippingCluster(feature: FeatureLike, resolution: number) {
  const size = feature.get("features").length;
  const features: Feature[] = feature.get("features");
  let radius = 16, alpha = 90;
  
  let fillColor = "", strokeColor = "";
  for (let i = 0; features && i < features.length; i++) {
    const feature = features[i];
    let style: any = feature.getStyle();
    if (Array.isArray(style)) style = style[0];
    
    const properties = feature.getProperties() as TPlanMarkerProperty;
    console.log("cluster properties", properties)
    if (properties.highlighted) {
      fillColor = `#${MINT}${alpha}`;
      break;
    }
    if (properties.hovered) {
      fillColor = `#${PRIMARY}${alpha}`;
      break;
    }
    if (!!properties.color) {
      fillColor = properties.color;
    }
    else if (style?.image_?.color_) {
      const fill = style?.image_?.color_;
      if (Array.isArray(fill) && fill?.length > 2) {
        fillColor = `rgba(${fill[0]}, ${fill[1]}, ${fill[2]}, ${alpha * 100})`;
      }
    }
    if (!properties.disabled) {
      break;
    }
    //disable feature 일 때
    console.log("styleproperties", properties, fillColor)
    strokeColor = fillColor;
    fillColor = `#${NEUTRAL}${alpha}`;
  }
  
  return createCluster(resolution, size, radius, fillColor, strokeColor, alpha);
}

const GAB = 0.6;  // 0 means opposite colors, 1 means same colors
export const getRandomNumber = (colorsToFilter?: string[]) => {
  let random = Math.floor(Math.random() * 16777215);
  
  let randomHex = random.toString(16);
  if (colorsToFilter) {
    let containSimilarColor = false
    for (let i in colorsToFilter) {
      const color = colorsToFilter[i];
      if (hexColorDelta(randomHex, color) > GAB) {
        containSimilarColor = true;
        break;
      }
    }
    if (containSimilarColor) randomHex = getRandomNumber(colorsToFilter);
  }
  return randomHex;
};

export const isKorean = (str) => {
  const regExp = /^[ㄱ-ㅎ|ㅏ-ㅣ|가-힣].*$/;
  if (regExp.test(str)) {
    return true;
  }
  return false;
};

export const isEnglish = (str) => {
  const regExp = /[a-zA-Z].+/;
  if (regExp.test(str)) {
    return true;
  }
  return false;
};

/**
 * Search params are held as an array of two elements arrays where member 0 is the key and member 1 is value
 * This function extracts the values from the array and returns an object with the key as the property name and the value as the property value
 * Where a key has multiple values, the value is an array of values
 *
 * @param {URLSearchParams} searchParams
 */
export function extractExistingParams<T = any>(searchParams: URLSearchParams): T {
  const entries = Array.from(searchParams.entries());
  console.log('extractExistingParams', entries)
  return entries.reduce((acc: any, a) => {
    console.log("extractExistingParams reduce", acc, a)
    return ((acc[a[0]] = acc[a[0]] || []).push(a[1]), acc)
  }, {});
}

/**
 * Remove a value from an existing parameter where the parameter can occur multiple times
 * If the value is the last value, the parameter is removed
 *
 * @param {URLSearchParams} searchParams
 * @param {string} key
 * @param {string} value
 */
export function removeExistingParamsArrayValue(searchParams, key, value) {
  const existingParams = extractExistingParams(searchParams);
  if (existingParams[key]) {
    existingParams[key] = existingParams[key].filter(v => v !== value);
  }
  if (existingParams[key]?.length === 0) {
    delete existingParams[key];
  }
  console.log("existingParams", existingParams)
  return existingParams;
}

export function isUpdateDisabled(status?: string, is_scanned?: boolean) {
  if (status && status.startsWith("RETURN")) return !status.startsWith("RETURN_CREATED") && !status.startsWith("RETURN_WAITINGFORSORT");
  return is_scanned;
}

export const getKeyFromDataGetSelectedData = (data: any[], keyField?: string, selectedKeys?: string[]) => {
  console.log('getKeyFromDataGetSelectedData 1', keyField, data)
  const keys: any = [];
  const key = keyField || "key";
  const selectedData: any = [];
  if (data) {
    data.forEach((d: any, index: number) => {
      if (d[key]) {
        keys.push(d[key]);
      }
      else {
        d[key] = index;
        keys.push(index);
      }
      if (selectedKeys) {
        selectedKeys.forEach((sKey) => {
          if (d[key] === sKey) selectedData.push(d);
        })
      }
      else {
        selectedData.push(d);
      }
    });
  }
  console.log('getKeyFromDataGetSelectedData', keyField, keys, data)
  return {
    selectedData, keys,
  };
};

export const isEmptyObject = (object: any) => {
  return !object || !(Object.keys(object).length > 0);
};

